import React from 'react';
import LoadingCircle from '@cpg-its/ads.common.loading-circle';
import { Formik, FormikProps } from 'formik';
import { Address, Parish, ParishFilters, ParishSearchSort } from './parish-model';
import { FormButtons } from '../form-element/form-buttons';
import { FormField } from '../../../../TCSMApp/global/components/form-element/form-field';
import { PaginatedList } from '../list/paginated-list';
import { useEpiParishSettings, useEpiParishSettingsLoaded } from '../../hooks/useEpiParishSettings';
import { GetParishOrderByOptions } from '../../ducks/parish-api';
import { Wysiwyg } from '../wysiwyg';
import { MapItAction } from './mapIt-action';

interface Props {    
    searchResults: Parish[];   
    onNewSearch: ()=> void;     
    onNavigateBack: ()=> void;
    searchFilters: ParishFilters;
    setSort: (searchFilters: ParishFilters, selectedSort : ParishSearchSort)=> void;    
    currentPageIndex: number;
    navigateToPageIndex: (pageIndex: number)=> void;
    selectParishById: (parishId: string | number) => void;
    searchInProgress: boolean;
}

function ParishResults(props: Props){ 
    const { searchResults, onNewSearch, onNavigateBack, searchFilters, setSort, 
        currentPageIndex, navigateToPageIndex, selectParishById,
        searchInProgress } = props;       
    const parishSettingsLoaded = useEpiParishSettingsLoaded();
    const parishSettings = useEpiParishSettings();    

    const orderByOptions = GetParishOrderByOptions(parishSettings?.orderByMapping);

    const cancelText = !searchInProgress && (!searchResults || searchResults?.length === 0) ? parishSettings?.modifySearchButton : parishSettings?.backButton;

    const initialValues = {
        orderBy: searchFilters?.sort,
    };

    function OnSortChanged(e){        
        setSort(searchFilters, e?.target?.value);
    }

    function OnSubmit(values){        
        setSort(searchFilters, values?.orderBy);
    }

    function SortSelector(){
        if (searchResults?.length > 0){
            return (<Formik
                initialValues={initialValues}
                onSubmit={OnSubmit}			
                enableReinitialize			
            >
                {(formProps: FormikProps<any>) => (
                    <FormField
                        name='orderBy'                            
                        component='input'  
                        placeholder={parishSettings?.orderByLabel || '- Order By -'}                           
                        touched={formProps.touched}
                        errors={formProps.errors}                           
                        onChange={(orderByChanged)=> OnSortChanged(orderByChanged)}                                                                                                            
                        options={orderByOptions}
                        select
                        />
                )}
			
		    </Formik >);
        }

        return null;
    }

    function ResultsLabel(): string{
        if (searchInProgress) return null;

        if (!searchInProgress && (!searchResults || searchResults?.length === 0)){
            return parishSettings?.noResultsMessage;
        }

        return `${searchInProgress ? '-' : searchResults?.length} ${parishSettings?.recordsFoundLabel || 'record(s) found'}`;
    }

    function NavigationButtons() {
        return (
        <>
            <div className='quickfind-parish-results-nav'>
                <p><strong> {ResultsLabel()} </strong></p>            
                    <div className='quickfind-parish-results-sort quickfind-parish-results-sort-mobile'>
                        <SortSelector />
                    </div>
            </div>                        
            <div className='quickfind-parish-results-formbuttons'>
                <FormButtons
                            disabled={false}
                            submitText={parishSettings?.newSearchButton || 'New Search'}
                            cancelText={cancelText}
                            onSubmit={onNewSearch}
                            onCancel={onNavigateBack}
                            submitDisabled={false}                    
                            hideCancelButton={!cancelText}
                    />
                    <div className='quickfind-parish-results-sort quickfind-parish-results-sort-desktop'>
                        <SortSelector />
                    </div>
            </div>
        </>);
    }    

    function RenderFax(fax: string){
        if (fax) {
            return (<div>{fax} {parishSettings?.faxTitle}</div> );
        }

        return ( <Wysiwyg content={parishSettings?.faxEmptyMessage} /> );
    }

    function RenderAddress(address: Address){
        if (!address) return null;

        return (<>
            <div>                                
                <div>{address.address1 || ''}</div>
                <div>{address.address2 || ''}</div>
                <div>{address.address3 || ''}</div>
                <div>{`${address.city ? `${address.city},` : '' } ${address.state || ''} ${address.postal_code || ''}`}</div>
                <div>{address.country || ''}</div>                
            </div>
        </>);
    }

    function ParishRow(parishItem: Parish, index: number){
        const indexRender = index + 1;
        return (<>
            <div className='quickfind-parish-results-item' key={parishItem?.id}>
                <div className='quickfind-parish-results-item-index' >{indexRender}</div>
                <div className='quickfind-parish-results-item-content'>
                    <div><button className='quickfind-parish-results-item-link' type='button' onClick={() => selectParishById(parishItem?.id)} key={parishItem?.id}>{parishItem?.name}</button></div>
                    <div>{RenderAddress(parishItem?.address)}</div>
                
                    <div>{parishItem?.number}</div>
                    { RenderFax(parishItem?.fax) }
                </div>

                <div>{parishItem?.type}</div>
                <div className='quickfind-parish-results-item-mapit'> <MapItAction mapsLink={parishItem?.maps_link} mapItTitle={parishSettings?.mapItTitle} /></div>                
            </div>
        </>);
    }   

    if (!parishSettingsLoaded || !parishSettings) {
        return <LoadingCircle />;
    }

    return (        
        <>
            <div>     
                <h3 className='ecd-page-content-title'>
                    {parishSettings?.searchResultTitle}
                </h3>         
                <div className='ecd-page-content-section'>
                    <NavigationButtons />                
                    <PaginatedList<Parish>
                        renderHeaders={() =>  null }
                        renderListItem={ParishRow}
                        completeList={searchResults}
                        currentPageIndex={currentPageIndex}
                        emptyMessage=''
                        navigateToPageIndex={navigateToPageIndex}
                        searchInProgress={searchInProgress} />
                </div>                  
            </div>            
        </>        
    );
}

export default ParishResults;
